import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ChatStreamEvent } from '@nexops/shared';
import type { TFunction } from '@/i18n/I18nProvider';
import { chatKeys } from '../api/chat.api';
import {
  applyIncomingMessage,
  applyMessageUpdate,
  clearConversationUnread,
  findConversation,
  markMessageDeleted,
  patchConversation,
  removeConversation,
  upsertConversation,
} from '../api/chat.cache';
import { useChatStore } from '../hooks/useChatStore';
import { fullName, isMuted } from './chat-utils';

export interface StreamContext {
  qc: QueryClient;
  meId: string;
  t: TFunction;
  navigate: (to: string, opts?: { replace?: boolean }) => void;
  /** Aktualna ścieżka aplikacji (do wykrycia otwartej rozmowy). */
  getPathname: () => string;
}

export const CHAT_EVENT_TYPES = [
  'ready',
  'message',
  'message.updated',
  'message.deleted',
  'conversation',
  'conversation.removed',
  'read',
  'typing',
] as const;

/** Rozpakowuje dane zdarzenia SSE; `type` bierze z nazwy zdarzenia, gdy nie ma go w JSON-ie. */
export function parseStreamEvent(name: string, raw: string): ChatStreamEvent | null {
  try {
    const data = JSON.parse(raw) as Record<string, unknown>;
    return { ...data, type: name } as unknown as ChatStreamEvent;
  } catch {
    return null;
  }
}

function openConversationId(pathname: string): string | null {
  const m = /^\/chat\/([^/?#]+)/.exec(pathname);
  return m?.[1] ?? null;
}

/** Odświeża wszystko po wznowieniu połączenia (zdarzenia z przerwy mogły przepaść). */
export function resyncAll(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: chatKeys.all });
}

export function handleChatEvent(event: ChatStreamEvent, ctx: StreamContext): void {
  const { qc, meId } = ctx;
  const store = useChatStore.getState();

  switch (event.type) {
    case 'ready':
      if (event.unread) qc.setQueryData(chatKeys.unread, event.unread);
      return;

    case 'message': {
      const { message } = event;
      const known = applyIncomingMessage(qc, message, meId);
      store.clearTyping(message.conversationId, message.sender.id);
      if (!known) {
        // Rozmowa jeszcze nieznana (np. dopiero nas dodano) — pobierz listy i licznik.
        void qc.invalidateQueries({ queryKey: ['chat', 'conversations'] });
        void qc.invalidateQueries({ queryKey: chatKeys.unread });
      }
      if (message.sender.id !== meId && message.kind !== 'SYSTEM') notifyIncoming(event, ctx);
      return;
    }

    case 'message.updated':
      applyMessageUpdate(qc, event.message);
      return;

    case 'message.deleted':
      markMessageDeleted(qc, event.conversationId, event.messageId);
      if (findConversation(qc, event.conversationId)?.lastMessage?.id === event.messageId) {
        patchConversation(qc, event.conversationId, (c) => (c.lastMessage ? { ...c, lastMessage: { ...c.lastMessage, snippet: '' } } : c));
      }
      return;

    case 'conversation':
      upsertConversation(qc, event.conversation);
      void qc.invalidateQueries({ queryKey: chatKeys.unread });
      return;

    case 'conversation.removed':
      removeConversation(qc, event.conversationId);
      void qc.invalidateQueries({ queryKey: chatKeys.unread });
      if (openConversationId(ctx.getPathname()) === event.conversationId) ctx.navigate('/chat', { replace: true });
      return;

    case 'read':
      if (event.userId === meId) {
        clearConversationUnread(qc, event.conversationId);
        void qc.invalidateQueries({ queryKey: chatKeys.unread });
      } else {
        patchConversation(qc, event.conversationId, (c) => ({
          ...c,
          participants: c.participants.map((p) => (p.user.id === event.userId ? { ...p, lastReadAt: event.at } : p)),
        }));
      }
      return;

    case 'typing':
      if (event.userId !== meId) store.markTyping(event.conversationId, event.userId);
      return;
  }

}

/** Toast o nowej wiadomości — tylko gdy nie patrzymy na tę rozmowę i nie jest wyciszona / zarchiwizowana. */
function notifyIncoming(event: Extract<ChatStreamEvent, { type: 'message' }>, ctx: StreamContext): void {
  const { message, conversationId } = event;
  const conv = findConversation(ctx.qc, conversationId);
  if (conv && (isMuted(conv) || conv.archived)) return;
  const viewing = openConversationId(ctx.getPathname()) === conversationId && document.visibilityState === 'visible';
  if (viewing) return;

  const text = message.body.trim() || (message.attachments.length ? ctx.t('chat.attachmentLabel') : '');
  const snippet = text.length > 100 ? `${text.slice(0, 100)}…` : text;
  toast(`${fullName(message.sender)}: ${snippet}`, {
    id: `chat-${conversationId}`,
    duration: 7000,
    action: { label: ctx.t('chat.toast.open'), onClick: () => ctx.navigate(`/chat/${conversationId}`) },
  });
}
